/**
 * Comprehensive Lead Scoring System
 * Combines: Storm impact + Roof size + Property value + Owner status + Home age
 *
 * IMPORTANT: "roofAge" from our data is actually BUILDING age unless
 * roofAgeConfidence is 'medium' or 'high' (from sale/permit heuristics).
 * Scoring adjusts weight based on confidence level.
 *
 * Score ranges:
 * - 80+: HOT (red badge, priority call)
 * - 60-79: WARM (orange badge)
 * - 40-59: COOL (standard)
 * - 0-39: LOW (deprioritize)
 */

// Scoring weights (total can exceed 100, normalized at end)
const WEIGHTS = {
  // Storm impact (most valuable for roofing - recent damage = immediate need)
  STORM_AFFECTED: 35,        // Base points for any storm impact
  STORM_SCORE_MULTIPLIER: 0.2, // Additional points from storm score (score/5)

  // Roof characteristics
  BIG_ROOF_2000: 15,         // Roof > 2000 sqft
  BIG_ROOF_3000: 25,         // Roof > 3000 sqft
  BIG_ROOF_4000: 35,         // Roof > 4000 sqft (premium job)

  // Home age (NOT roof age - reduced weight since roof may have been replaced)
  HOME_OLD_20: 10,           // Home > 20 years old (roof MIGHT be original)
  HOME_OLD_30: 15,           // Home > 30 years old
  HOME_OLD_40: 20,           // Home > 40 years old

  // Verified roof age (from permits, sale heuristics, or tax improvement data)
  VERIFIED_ROOF_OLD_15: 20,  // Verified roof > 15 years
  VERIFIED_ROOF_OLD_20: 30,  // Verified roof > 20 years
  VERIFIED_ROOF_OLD_25: 40,  // Verified roof > 25 years (definitely needs work)

  // Property value (higher value = can afford quality work)
  VALUE_150K: 5,             // > $150K
  VALUE_250K: 10,            // > $250K
  VALUE_400K: 15,            // > $400K
  VALUE_600K: 20,            // > $600K (premium)

  // Owner status
  OWNER_OCCUPIED: 20,        // Much easier to reach/convert

  // Data quality
  ENRICHED: 5,               // Has detailed data
  SATELLITE_MEASURED: 5,     // Accurate roof measurement
};

/**
 * Calculate comprehensive lead score
 * @param {Object} lead - Lead object with all available data
 * @param {Object} stormData - Optional storm check result { affected, score, storms }
 * @returns {Object} { score, breakdown, quality }
 */
export function calculateLeadScore(lead, stormData = null) {
  let score = 0;
  const breakdown = [];

  // === STORM IMPACT (highest priority for roofing leads) ===
  const isStormAffected = stormData?.affected || lead.stormAffected;
  const stormScore = stormData?.score || lead.stormScore || 0;

  if (isStormAffected) {
    score += WEIGHTS.STORM_AFFECTED;
    breakdown.push({ factor: 'Storm Zone', points: WEIGHTS.STORM_AFFECTED });

    // Additional points based on storm severity
    const stormBonus = Math.round(stormScore * WEIGHTS.STORM_SCORE_MULTIPLIER);
    if (stormBonus > 0) {
      score += Math.min(stormBonus, 20); // Cap at 20 extra points
      breakdown.push({ factor: 'Storm Severity', points: Math.min(stormBonus, 20) });
    }
  }

  // === ROOF SIZE (bigger = more valuable job) ===
  const roofSqft = lead.roofSqft || lead.roof_sqft || lead.estRoofSqft || 0;

  if (roofSqft >= 4000) {
    score += WEIGHTS.BIG_ROOF_4000;
    breakdown.push({ factor: 'Large Roof (4000+)', points: WEIGHTS.BIG_ROOF_4000 });
  } else if (roofSqft >= 3000) {
    score += WEIGHTS.BIG_ROOF_3000;
    breakdown.push({ factor: 'Big Roof (3000+)', points: WEIGHTS.BIG_ROOF_3000 });
  } else if (roofSqft >= 2000) {
    score += WEIGHTS.BIG_ROOF_2000;
    breakdown.push({ factor: 'Good Roof Size (2000+)', points: WEIGHTS.BIG_ROOF_2000 });
  }

  // === HOME AGE / ROOF AGE (confidence-aware) ===
  const roofAge = lead.roofAge || lead.roof_age || 0;
  const yearBuilt = lead.yearBuilt || lead.year_built || 0;
  const buildingAge = yearBuilt ? (new Date().getFullYear() - yearBuilt) : roofAge;
  const roofAgeConfidence = lead.roofAgeConfidence || lead.roof_age_confidence || 'low';

  if (roofAgeConfidence === 'medium' || roofAgeConfidence === 'high') {
    // We have actual evidence (sale heuristic, tax data, or permit)
    const verifiedAge = roofAge || buildingAge;
    if (verifiedAge >= 25) {
      score += WEIGHTS.VERIFIED_ROOF_OLD_25;
      breakdown.push({ factor: 'Verified Old Roof (25+yr)', points: WEIGHTS.VERIFIED_ROOF_OLD_25 });
    } else if (verifiedAge >= 20) {
      score += WEIGHTS.VERIFIED_ROOF_OLD_20;
      breakdown.push({ factor: 'Verified Aging Roof (20+yr)', points: WEIGHTS.VERIFIED_ROOF_OLD_20 });
    } else if (verifiedAge >= 15) {
      score += WEIGHTS.VERIFIED_ROOF_OLD_15;
      breakdown.push({ factor: 'Verified Mature Roof (15+yr)', points: WEIGHTS.VERIFIED_ROOF_OLD_15 });
    }
  } else {
    // Low confidence: only building age, much lower weight
    if (buildingAge >= 40) {
      score += WEIGHTS.HOME_OLD_40;
      breakdown.push({ factor: 'Old Home (40+yr, roof unknown)', points: WEIGHTS.HOME_OLD_40 });
    } else if (buildingAge >= 30) {
      score += WEIGHTS.HOME_OLD_30;
      breakdown.push({ factor: 'Older Home (30+yr, roof unknown)', points: WEIGHTS.HOME_OLD_30 });
    } else if (buildingAge >= 20) {
      score += WEIGHTS.HOME_OLD_20;
      breakdown.push({ factor: 'Home 20+yr (roof unknown)', points: WEIGHTS.HOME_OLD_20 });
    }
  }

  // === PROPERTY VALUE ===
  const value = lead.assessedValue || lead.assessed_value || 0;

  if (value >= 600000) {
    score += WEIGHTS.VALUE_600K;
    breakdown.push({ factor: 'Premium Property', points: WEIGHTS.VALUE_600K });
  } else if (value >= 400000) {
    score += WEIGHTS.VALUE_400K;
    breakdown.push({ factor: 'High Value Property', points: WEIGHTS.VALUE_400K });
  } else if (value >= 250000) {
    score += WEIGHTS.VALUE_250K;
    breakdown.push({ factor: 'Good Value Property', points: WEIGHTS.VALUE_250K });
  } else if (value >= 150000) {
    score += WEIGHTS.VALUE_150K;
    breakdown.push({ factor: 'Standard Property', points: WEIGHTS.VALUE_150K });
  }

  // === OWNER OCCUPIED ===
  if (lead.ownerOccupied || lead.owner_occupied) {
    score += WEIGHTS.OWNER_OCCUPIED;
    breakdown.push({ factor: 'Owner Occupied', points: WEIGHTS.OWNER_OCCUPIED });
  }

  // === DATA QUALITY BONUSES ===
  if (lead.enriched) {
    score += WEIGHTS.ENRICHED;
    breakdown.push({ factor: 'Enriched Data', points: WEIGHTS.ENRICHED });
  }

  if (lead.roofSqftMeasured || lead.roof_sqft_measured) {
    score += WEIGHTS.SATELLITE_MEASURED;
    breakdown.push({ factor: 'Satellite Measured', points: WEIGHTS.SATELLITE_MEASURED });
  }

  // === NORMALIZE SCORE (0-100 scale) ===
  const maxPossible =
    WEIGHTS.STORM_AFFECTED + 20 + // Storm max
    WEIGHTS.BIG_ROOF_4000 +       // Roof size max
    WEIGHTS.VERIFIED_ROOF_OLD_25 + // Roof age max (verified)
    WEIGHTS.VALUE_600K +          // Value max
    WEIGHTS.OWNER_OCCUPIED +      // Owner occupied
    WEIGHTS.ENRICHED +            // Enriched
    WEIGHTS.SATELLITE_MEASURED;   // Measured

  const normalizedScore = Math.min(100, Math.round((score / maxPossible) * 100));

  // === DETERMINE QUALITY TIER ===
  let quality = 'standard';
  if (normalizedScore >= 80 || (isStormAffected && normalizedScore >= 60)) {
    quality = 'hot';
  } else if (normalizedScore >= 60 || (isStormAffected && normalizedScore >= 40)) {
    quality = 'warm';
  } else if (normalizedScore >= 40) {
    quality = 'cool';
  }

  return {
    score: normalizedScore,
    rawScore: score,
    breakdown,
    quality,
    isStormAffected,
    roofSqft,
    roofAge: roofAge || buildingAge,
    roofAgeConfidence,
    propertyValue: value
  };
}

/**
 * Apply scoring to a lead and update its properties
 * @param {Object} lead - Lead to score
 * @param {Object} stormData - Optional storm data
 * @returns {Object} Lead with updated score and quality
 */
export function scoreLead(lead, stormData = null) {
  const result = calculateLeadScore(lead, stormData);

  return {
    ...lead,
    score: result.score,
    leadQuality: result.quality,
    scoreBreakdown: result.breakdown,
    stormAffected: result.isStormAffected,
    stormScore: stormData?.score || lead.stormScore || 0
  };
}

/**
 * Sort leads by score (hot first, then by score descending)
 * @param {Array} leads - Array of leads
 * @returns {Array} Sorted leads
 */
export function sortLeadsByScore(leads) {
  return [...leads].sort((a, b) => {
    // DB leads go to bottom
    if (a.inDatabase && !b.inDatabase) return 1;
    if (!a.inDatabase && b.inDatabase) return -1;

    // Storm-affected leads first
    if (a.stormAffected && !b.stormAffected) return -1;
    if (!a.stormAffected && b.stormAffected) return 1;

    // Then by quality tier
    const qualityOrder = { hot: 0, warm: 1, cool: 2, standard: 3 };
    const qualityDiff = (qualityOrder[a.leadQuality] || 3) - (qualityOrder[b.leadQuality] || 3);
    if (qualityDiff !== 0) return qualityDiff;

    // Then by score
    return (b.score || 0) - (a.score || 0);
  });
}

export default { calculateLeadScore, scoreLead, sortLeadsByScore };
